import { Paths } from '../../../types/react-help';
import { useMerchant } from '../use-merchant';

import { MerchantInfo } from './types';
import { useFullUser } from './use-user';

type ToRule<T extends object> = {
  [P in keyof T]?: boolean | (T[P] extends object ? ToRule<T[P]> : boolean);
};

/** Описывает модель доступа */
type AccessRule = ToRule<{
  settings: {
    contract: {
      newPayment: boolean;
    };
    token: {
      createToken: boolean;
      deleteToken: boolean;
    };
    whitelist: {
      upload: boolean;
    };
  };
  withdrawals: {
    payoffs: boolean;
    clientPayout: boolean;
  };
  transactionManagement: {
    transactions: {
      createInvoice: boolean;
      transactionModal: {
        return: boolean;
      };
    };
  };
}>;

const defaultRule: AccessRule = {
  settings: false,
  withdrawals: false,
  transactionManagement: false,
};

const ownerRule: AccessRule = {
  settings: true,
  withdrawals: true,
  transactionManagement: true,
};

const partnerManagerAccess: AccessRule = {
  settings: false,
  withdrawals: false,
  transactionManagement: {
    transactions: {
      createInvoice: false,
      transactionModal: {
        return: true,
      },
    },
  },
};

export const useAccessRule = (): AccessRule => {
  const { merchantInfo } = useFullUser();
  const { loading, merchant } = useMerchant();

  if (loading || !merchant) {
    return defaultRule;
  }

  const getRuleByRole = (inMerchantInfo: MerchantInfo | null) => {
    switch (inMerchantInfo?.role) {
      case 'manager':
        return partnerManagerAccess;
      case 'owner':
        return ownerRule;
      default:
        return defaultRule;
    }
  };

  const rule = getRuleByRole(merchantInfo);

  return {
    ...rule,
    transactionManagement: {
      transactions: {
        createInvoice: getRuleValue(rule, 'transactionManagement.transactions.createInvoice'),
        transactionModal: {
          return:
            merchant.refund_enabled && getRuleValue(rule, 'transactionManagement.transactions.transactionModal.return'),
        },
      },
    },
    withdrawals: {
      clientPayout: merchant.mass_withdraw_enabled && getRuleValue(rule, 'withdrawals.clientPayout'),
      payoffs: merchant.withdraw_enabled ? getRuleValue(rule, 'withdrawals.payoffs') : false,
    },
  };
};

export const getRuleValue = (rule: AccessRule, path: Paths<AccessRule>): boolean => {
  let currentNode: any = rule;

  for (const key of path.split('.')) {
    if (typeof currentNode[key] === 'boolean') {
      return currentNode[key];
    }

    currentNode = currentNode[key];
  }

  throw new Error(`Can't find path rule ${path}`);
};
